import React, { memo, useEffect, useState, useRef } from "react";
import { DeviceEventEmitter, View } from "react-native";
import { SceneMap, TabView } from "react-native-tab-view";
import VDBText from "@components/atoms/Text";
import ProductTypes from "../../molecules/ProductTypesTab/ProductTypes";
import { TabViewProps } from "./types";
import RequestHistory from "@components/molecules/RequestHistory/RequestHistory";
import SavedSearch from "@components/molecules/SavedSearchTab/SavedSearch";
import { VDBTabBar } from "@components/atoms/TabView/components/VDBTabBar";
import { useScrollContext } from "@navigations/utils/ScrollContext";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { useDispatch } from "react-redux";

const CustomTabView = (props: TabViewProps) => {
  const [_, handleReset] = useScrollContext();
  const initialIndex = props.selectedTab === "sent" ? 1 : 0;
  const [index, setIndex] = useState(initialIndex);
  const prevIndexRef = useRef(initialIndex);
  const isMountedRef = useRef(true);
  const userInitiatedRef = useRef(false);
  const userInitiatedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const jumpToRef = useRef<((key: string) => void) | null>(null);

  const userInfo = useUserInfo();
  const dispatch = useDispatch();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (userInitiatedTimerRef.current) {
        clearTimeout(userInitiatedTimerRef.current);
      }
    };
  }, []);

  const markUserInitiated = () => {
    userInitiatedRef.current = true;
    if (userInitiatedTimerRef.current) {
      clearTimeout(userInitiatedTimerRef.current);
    }

    userInitiatedTimerRef.current = setTimeout(() => {
      userInitiatedRef.current = false;
    }, 500);
  };

  useEffect(() => {
    const exploreButtonListener = DeviceEventEmitter.addListener(
      "EXPLORE_BUTTON",
      () => {
        if (!isMountedRef.current) return;
        setIndex(0);
        prevIndexRef.current = 0;
      }
    );

    const resetExplorePageListener = DeviceEventEmitter.addListener(
      "RESET_EXPLORE_PAGE_ACTIVE_INDEX",
      () => {
        if (!isMountedRef.current) return;
        setIndex(0);
        prevIndexRef.current = 0;
      }
    );

    return () => {
      exploreButtonListener.remove();
      resetExplorePageListener.remove();
    };
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;
    const newIndex = props.selectedTab === "sent" ? 1 : 0;
    setIndex(newIndex);
    prevIndexRef.current = newIndex;
    props.onIndexChange?.(newIndex);
  }, [props.selectedTab]);

  const renderScene = SceneMap({
    explore: ProductTypes,
    saved_searches: () => <SavedSearch index={index} />,
    recentsearch: RecentSearchRoute,
    received: () => (
      <ReceivedRequestHistory
        index={index}
        requestedItem={props?.requestedItem}
      />
    ),
    sent: () => (
      <SentRequestHistory index={index} requestedItem={props?.requestedItem} />
    ),
  });

  const [tabViewKey, setTabViewKey] = useState(0);

  const handleTabChange = (_index: number) => {
    if (!isMountedRef.current) return;
    if (!userInitiatedRef.current) {
      if (_index !== index) {
        const currentKey = props.tabOptions[index]?.key;
        if (currentKey) {
          requestAnimationFrame(() => {
            if (isMountedRef.current) jumpToRef.current?.(currentKey);
          });
        }
      }
      return;
    }
    userInitiatedRef.current = false;
    if (userInitiatedTimerRef.current) {
      clearTimeout(userInitiatedTimerRef.current);
    }
    const tabKey = props.tabOptions[_index]?.key;
    if (tabKey === "saved_searches" && userInfo.isGuest) {
      dispatch(setLoginModalType("default"));
      setIndex(prevIndexRef.current);
      setTabViewKey((k) => k + 1);
      return;
    }
    setIndex(_index);
    prevIndexRef.current = _index;
    handleReset();
    props.onIndexChange?.(_index);
  };
  return (
    <TabView
      key={tabViewKey}
      {...props}
      lazy={!props.loadNextTab}
      navigationState={{ index, routes: props.tabOptions }}
      renderScene={renderScene}
      swipeEnabled={false}
      animationEnabled={false}
      onIndexChange={handleTabChange}
      renderTabBar={(tabBarProps) => {
        jumpToRef.current = tabBarProps.jumpTo;
        return (
          <VDBTabBar
            tabBarProps={tabBarProps}
            props={props}
            onUserTabPress={markUserInitiated}
          />
        );
      }}
    />
  );
};

const ReceivedRequestHistory = memo(
  ({ index, requestedItem }: { index: number; requestedItem?: any }) => (
    <RequestHistory
      requestType={"received"}
      selectedIndex={index}
      requestedItem={requestedItem}
    />
  )
);

const SentRequestHistory = memo(
  ({ index, requestedItem }: { index: number; requestedItem?: any }) => (
    <RequestHistory
      requestType={"sent"}
      selectedIndex={index}
      requestedItem={requestedItem}
    />
  )
);

const RecentSearchRoute = () => (
  <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
    <VDBText>Recent Searches</VDBText>
  </View>
);

export default memo(CustomTabView);
